import { useEffect, useRef } from 'react';
import { AccessibilityInfo } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatRestTime } from '@heraion/shared';

import { readClock, type TimerClock } from '../timerClock';

import { FocusPanelRest } from './FocusPanelRest';
import { RestInviteRow } from './RestInviteRow';
import { LoggerDockBand } from './LoggerDockBand';
import { useDockSlot } from './LoggerDock';
import type { useRestTimer } from './useRestTimer';
import type { RestInvite } from './useRestInvite';
import type { useTheme } from './theme';

/** The running rest as the screen hands it over. The one DECLARED spelling of
 *  the shape `useTimelineLogger` builds; it lived on FocusPanel's props until
 *  the rest left the card. */
export type LoggerRest = {
  restTimer: ReturnType<typeof useRestTimer>;
  /** "→ Set 3/4" / "→ <next exercise>". Null for a rest started by hand. */
  cueText: string | null;
  /** #308: the caption names the athlete's Pin, not the queue. */
  cuePinned: boolean;
  onOpenRestPicker: () => void;
  restPickerSaving: boolean;
  /** The longer-rest offer after a set near failure, or null. */
  invite: (RestInvite & { onAccept: () => void }) | null;
};

// TM-18c — the rest, DOCKED. It used to take the effort meter's two rows
// inside the focused card, which is why the whole logger had to freeze: the
// countdown was sitting on the surface the athlete wanted to correct. Out
// here it blocks nothing, and the card gets its meter back for the whole
// rest — correcting a set you just closed is normal and wanted (§2/§3).
//
// Not a Modal: the two siblings that were already there
// (TimelineHistorySheet, LoggerCommitPrompts) are both transparent overlays
// with their own backdrop, and this is the opposite — a bar that must never
// eat the touches of the page behind it.
//
// ⭐ LG-94 — it no longer positions ITSELF. It hands its band to the screen's
// bottom dock, which owns the one absolute position, stacks whatever is in it,
// measures the total and reserves it. Before, this bar and the load controls
// would each have claimed `bottom: 0` and drawn on top of each other; the
// athlete's own screenshot at 16:20 shows the rest band and the keyboard up at
// the same time, so "they never coexist" was not available as an answer.
//
// ⛔ The height is still MEASURED, never a constant — the dock does the
// measuring now. It moves with the system font scale, the locale, and whether
// the caption line is there at all; a number that fits the phone it was
// written on is wrong on every other one
// ([[feedback_derive_thresholds_from_data_not_constants]]).
//
// ⛔ No safe-area inset of its own. LoggerScreen already wraps this whole
// tree in a SafeAreaView that consumes the bottom edge once; claiming it a
// second time would double the home-indicator gap.
export function LoggerRestBar({
  rest,
  theme,
}: {
  /** The running rest, or null. */
  rest: LoggerRest | null;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();

  const resting = rest !== null;

  // A rest starting and a rest ending are whole-screen state changes, and a
  // sighted athlete gets both for free — the bar slides in, then out. A screen
  // reader whose cursor is anywhere else gets NOTHING from a docked landmark,
  // which is passive by definition.
  //
  // ⚠️ This announcement is not new. The freeze used to make it ("Editing
  // locked while resting"), and TM-18b deleted the freeze with the sentence
  // still inside it — the wording was about the lock, but the SIGNAL was about
  // the rest, and only the wording died with the lock.
  // The seconds are read off the clock at the moment of the announcement: the
  // timer's state no longer carries a number that moves (#326).
  const clockRef = useRef<TimerClock | null>(null);
  clockRef.current = rest?.restTimer.state.clock ?? null;
  const wasResting = useRef(false);
  useEffect(() => {
    const was = wasResting.current;
    wasResting.current = resting;
    if (was === resting) return;
    const clock = clockRef.current;
    const startSec = clock ? readClock(clock, Date.now()).remainingSec : 0;
    AccessibilityInfo.announceForAccessibility(
      resting
        ? t('mobile.logger.restStartedA11y', {
            time: formatRestTime(startSec),
            defaultValue: 'Rest started, {{time}}.',
          })
        : t('mobile.logger.restEndedA11y', { defaultValue: 'Rest over.' }),
    );
  }, [resting, t]);

  // ⛔ No early return: the slot has to be written on the way OUT too, and a
  // component that returned first would leave its band docked forever.
  const band =
    rest === null ? null : (
      <LoggerDockBand
        theme={theme}
        accessibilityLabel={t('mobile.logger.restBarA11y', {
          defaultValue: 'Rest timer',
        })}
      >
        {rest.invite ? (
          <RestInviteRow
            key={rest.invite.key}
            sec={rest.invite.sec}
            inviteKey={rest.invite.key}
            onAccept={rest.invite.onAccept}
            theme={theme}
            t={t}
          />
        ) : null}
        <FocusPanelRest
          restTimer={rest.restTimer}
          cueText={rest.cueText}
          cuePinned={rest.cuePinned}
          onOpenRestPicker={rest.onOpenRestPicker}
          restPickerSaving={rest.restPickerSaving}
          theme={theme}
        />
      </LoggerDockBand>
    );

  useDockSlot('rest', band);
  return null;
}
